import { StyledMenuItem } from '@components/display/menu';
import { IOSSwitch } from '@components/utility/switches';
import { APP_PREFIX_PATH } from '@constants/common';
import GridViewIcon from '@mui/icons-material/GridView';
import ListIcon from '@mui/icons-material/List';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import { IconButton, Link, Menu, Breadcrumbs as MUIBreadcrumbs, Skeleton, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';

const MEDIA_BROWSER_VIEW_STORAGE_KEY = 'media-browser-view';

const getHomeLinkProps = (pathname: string) => ({
    to: APP_PREFIX_PATH,
    replace: pathname.replace(/\/+$/, '') === APP_PREFIX_PATH,
});

export type BreadcrumbItem = {
    label: string | React.ReactNode;
    to: string;
    loading?: boolean;
    skipParams?: boolean;
};

type BreadcrumbsProps = {
    path: BreadcrumbItem[];
    maxItems?: number;
    collapsedVisibleItems?: number;
    extraButton?: React.ReactNode;
    /** When true, renders a grid/list view switcher backed by the `view` query param */
    viewSwitcher?: boolean;
};

export const Breadcrumbs = ({
    path,
    maxItems = 3,
    collapsedVisibleItems = 2,
    extraButton,
    viewSwitcher,
}: BreadcrumbsProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [params, setParams] = useSearchParams();
    const { pathname } = useLocation();
    const homeLinkProps = getHomeLinkProps(pathname);
    const viewParam = params.get('view');
    const [savedView, setSavedView] = useState<'grid' | 'list'>(() => {
        return window.localStorage.getItem(MEDIA_BROWSER_VIEW_STORAGE_KEY) === 'list' ? 'list' : 'grid';
    });
    const view = viewParam === 'list' || viewParam === 'grid' ? viewParam : savedView;

    const open = Boolean(anchorEl);
    const trailingVisibleCount = Math.max(1, Math.floor(collapsedVisibleItems));
    const visibleTailCount = Math.min(trailingVisibleCount, Math.max(path.length - 1, 0));
    const firstVisibleTailIndex = Math.max(path.length - visibleTailCount, 1);
    const shouldCollapse = path.length > maxItems && firstVisibleTailIndex > 1;
    const menuItems = shouldCollapse ? path.slice(1, firstVisibleTailIndex) : [];
    const visibleItems = shouldCollapse ? [path[0], ...path.slice(firstVisibleTailIndex)] : path;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement> | null) => {
        if (event) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const withCurrentParams = (to: string, skipParams?: boolean) => {
        return `${to}${!skipParams && params.toString() ? `?${params.toString()}` : ''}`;
    };

    useEffect(() => {
        if (!viewSwitcher || viewParam === 'list' || viewParam === 'grid') {
            return;
        }

        setParams(
            (previousParams) => {
                const nextParams = new URLSearchParams(previousParams);
                nextParams.set('view', savedView);
                return nextParams;
            },
            { replace: true }
        );
    }, [savedView, setParams, viewParam, viewSwitcher]);

    const handleViewChange = () => {
        const nextView = view === 'list' ? 'grid' : 'list';
        window.localStorage.setItem(MEDIA_BROWSER_VIEW_STORAGE_KEY, nextView);
        setSavedView(nextView);
        setParams(
            (previousParams) => {
                const nextParams = new URLSearchParams(previousParams);
                nextParams.set('view', nextView);
                return nextParams;
            },
            { replace: true }
        );
    };

    if (visibleItems.length === 0) {
        return null;
    }

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            px={{ xs: '20px', md: '55px' }}
            bgcolor="background.paper"
            py="15px"
            borderBottom={1}
            borderRight={1}
            borderColor="rgba(0,0,0,0.16)"
        >
            <Stack direction="row" alignItems="center" gap="6px">
                <IconButton component={RouterLink} {...homeLinkProps} className="p-0">
                    <SpaceDashboardOutlinedIcon
                        sx={{ transform: 'rotate(90deg) scale(1, -1)', fontSize: 20, fill: '#707579' }}
                    />
                </IconButton>
                <NavigateNextIcon fontSize="small" color="black" />
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    classes={{ paper: 'rounded-lg', list: 'p-0' }}
                >
                    {menuItems.map((item, index) => (
                        <StyledMenuItem key={item.to} onClick={handleClose} underline={index !== menuItems.length - 1}>
                            <Link
                                component={RouterLink}
                                to={withCurrentParams(item.to, item.skipParams)}
                                color="neutral-600"
                                underline="none"
                                sx={{ width: '100%' }}
                            >
                                {item.label}
                            </Link>
                        </StyledMenuItem>
                    ))}
                </Menu>
                <MUIBreadcrumbs
                    separator={<NavigateNextIcon fontSize="small" color="black" />}
                    aria-label="breadcrumb"
                    classes={{ separator: 'mx-[6px]' }}
                >
                    <Link
                        {...(visibleItems.length === 1
                            ? { color: 'text.primary', underline: 'none' }
                            : {
                                  color: 'neutral-600',
                                  to: withCurrentParams(visibleItems[0].to, visibleItems[0].skipParams),
                                  component: RouterLink,
                                  underline: 'hover',
                              })}
                        fontSize={14}
                        fontWeight={500}
                    >
                        {visibleItems[0].label}
                    </Link>
                    {shouldCollapse && (
                        <IconButton color="neutral-600" size="small" onClick={handleClick} className="p-0">
                            <MoreHorizIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    )}
                    {visibleItems.length > 1 &&
                        visibleItems.slice(1).map((item, index) =>
                            item.loading ? (
                                <Skeleton key={`${item.to}-loading`} variant="text" width={100} />
                            ) : (
                                <Link
                                    {...(index + 1 === visibleItems.length - 1
                                        ? { color: 'text.primary', underline: 'none' }
                                        : {
                                              color: 'neutral-600',
                                              to: withCurrentParams(item.to, item.skipParams),
                                              component: RouterLink,
                                              underline: 'hover',
                                          })}
                                    key={item.to}
                                    fontSize={14}
                                    fontWeight={500}
                                >
                                    {item.label}
                                </Link>
                            )
                        )}
                </MUIBreadcrumbs>
            </Stack>
            {extraButton || viewSwitcher ? (
                <Stack direction="row" alignItems="center" gap="6px">
                    {extraButton}
                    {viewSwitcher && (
                        <IOSSwitch
                            size="large"
                            LeftIcon={GridViewIcon}
                            RightIcon={ListIcon}
                            noShadow
                            checked={view === 'list'}
                            onChange={handleViewChange}
                        />
                    )}
                </Stack>
            ) : null}
        </Stack>
    );
};
